import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Palette, Trash, PencilSimple, FloppyDisk, Plus, Star, Image as ImageIcon, UploadSimple } from "@phosphor-icons/react";

export default function ReportTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited or "new"
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/report-templates");
      setItems(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try {
      await api.delete(`/report-templates/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-[#005CFF]/15 to-purple-500/10 border border-[#005CFF]/30 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#005CFF]/20 flex items-center justify-center">
          <Palette size={22} className="text-[#005CFF]" weight="duotone" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold">Modèles de rapport</div>
          <div className="text-xs text-white/70">Logo, couleurs, en-tête et pied de page pour vos PDFs.</div>
        </div>
        <Button
          onClick={() => setEditing({ __new: true, name: "", intervention_type: "", primary_color: "#005CFF", accent_color: "#0A0A0A", header_text: "", footer_text: "", is_default: false })}
          className="h-10 bg-[#005CFF] hover:bg-[#0047CC] rounded-xl"
          data-testid="report-tpl-new"
        >
          <Plus size={16} weight="bold" /> Nouveau
        </Button>
      </div>

      {loading && <div className="text-xs text-white/40 p-4 text-center">Chargement…</div>}
      {!loading && items.length === 0 && (
        <div className="rounded-2xl bg-white/[0.03] border border-dashed border-white/10 p-6 text-center text-sm text-white/40">
          Aucun modèle. Créez-en un pour personnaliser vos rapports PDF.
        </div>
      )}

      <div className="grid gap-2">
        {items.map((t) => (
          <div key={t.id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center gap-3" data-testid={`report-tpl-${t.id}`}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: t.primary_color + "33", border: `1px solid ${t.primary_color}` }}>
              {t.logo_path ? (
                <img src={`${API}/report-templates/${t.id}/logo`} alt="" className="w-9 h-9 object-contain" />
              ) : (
                <ImageIcon size={18} className="text-white/60" weight="duotone" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-display font-semibold text-sm truncate">{t.name}</span>
                {t.is_default && <Star size={12} weight="fill" className="text-amber-400" />}
              </div>
              <div className="text-[10px] text-white/50 truncate">
                {t.intervention_type || "Type non défini"} · <span className="font-mono">{t.primary_color}</span> / <span className="font-mono">{t.accent_color}</span>
              </div>
            </div>
            <button onClick={() => setEditing(t)} className="p-2 rounded-lg hover:bg-white/5 text-white/60" data-testid={`report-tpl-edit-${t.id}`}><PencilSimple size={14} /></button>
            <button onClick={() => remove(t.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" data-testid={`report-tpl-del-${t.id}`}><Trash size={14} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <ReportTemplateEditor
          template={editing}
          onSaved={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ReportTemplateEditor({ template, onSaved, onCancel }) {
  const isNew = template.__new;
  const [form, setForm] = useState({
    name: template.name || "",
    intervention_type: template.intervention_type || "",
    primary_color: template.primary_color || "#005CFF",
    accent_color: template.accent_color || "#0A0A0A",
    header_text: template.header_text || "",
    footer_text: template.footer_text || "",
    is_default: !!template.is_default,
  });
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom obligatoire");
    setSaving(true);
    try {
      let tid = template.id;
      if (isNew) {
        const r = await api.post("/report-templates", form);
        tid = r.data.id;
      } else {
        await api.patch(`/report-templates/${tid}`, form);
      }
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post(`/report-templates/${tid}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(isNew ? "Modèle créé" : "Modèle mis à jour");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-[#005CFF]/40 p-4 space-y-3">
      <div className="text-[11px] uppercase tracking-widest text-[#005CFF]">
        {isNew ? "Nouveau modèle" : `Édition — ${template.name}`}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <F label="Nom *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10" data-testid="tpl-name" /></F>
        <F label="Type d'intervention"><Input value={form.intervention_type} onChange={(e) => set("intervention_type", e.target.value)} placeholder="ex: Électricité générale" className="bg-white/5 border-white/10" data-testid="tpl-type" /></F>
        <F label="Couleur primaire">
          <div className="flex items-center gap-2">
            <input type="color" value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="w-11 h-10 rounded-lg cursor-pointer" data-testid="tpl-primary" />
            <Input value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="bg-white/5 border-white/10 font-mono" />
          </div>
        </F>
        <F label="Couleur bandeau">
          <div className="flex items-center gap-2">
            <input type="color" value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="w-11 h-10 rounded-lg cursor-pointer" data-testid="tpl-accent" />
            <Input value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="bg-white/5 border-white/10 font-mono" />
          </div>
        </F>
      </div>
      <F label="En-tête HTML (haut du PDF)">
        <Textarea value={form.header_text} onChange={(e) => set("header_text", e.target.value)} rows={2} placeholder="<b>BKPRO Électricité</b><br/>SIREN 123 456 789" className="bg-white/5 border-white/10 font-mono text-xs" data-testid="tpl-header" />
      </F>
      <F label="Pied de page (bas du PDF)">
        <Input value={form.footer_text} onChange={(e) => set("footer_text", e.target.value)} placeholder="ex: 12 rue de l'Électricité 34130 · contact@bkpro.fr" className="bg-white/5 border-white/10" data-testid="tpl-footer" />
      </F>
      <F label="Logo (PNG/JPEG, max 5 Mo)">
        <div className="flex items-center gap-2">
          <label className="flex-1 flex items-center gap-2 rounded-xl bg-white/5 border border-dashed border-white/20 p-3 cursor-pointer hover:border-[#005CFF]/40">
            <UploadSimple size={16} className="text-white/60" />
            <span className="text-xs text-white/60 truncate">{logoFile ? logoFile.name : "Sélectionner un fichier"}</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setLogoFile(f);
                const rd = new FileReader();
                rd.onload = () => setLogoPreview(rd.result);
                rd.readAsDataURL(f);
              }}
              data-testid="tpl-logo-input"
            />
          </label>
          {(logoPreview || (!isNew && template.logo_path)) && (
            <img
              src={logoPreview || `${API}/report-templates/${template.id}/logo`}
              alt=""
              className="w-14 h-14 object-contain rounded-lg bg-white p-1"
            />
          )}
        </div>
      </F>
      <div className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 p-3">
        <Switch checked={form.is_default} onCheckedChange={(v) => set("is_default", v)} data-testid="tpl-default" />
        <div className="text-xs">
          <div>Modèle par défaut</div>
          <div className="text-white/40 text-[10px]">Appliqué automatiquement aux nouveaux chantiers</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} className="border border-white/10 h-11">Annuler</Button>
        <Button onClick={save} disabled={saving} className="bg-[#005CFF] hover:bg-[#0047CC] h-11" data-testid="tpl-save">
          <FloppyDisk size={14} weight="bold" /> {saving ? "…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-white/60 text-[11px] uppercase tracking-widest">{label}</Label>
      {children}
    </div>
  );
}
